import { observer } from "mobx-react-lite"
import { Clock, Info, Target, TrendingUp } from "lucide-react"

import type { VoteOptimizationStrategyType } from "@/domains/vote-optimizer/vote-optimization-strategy-factory"
import { Button } from "@/components/button"
import { SuperVoteLogo } from "@/components/super-vote-logo"
import { RiskProgressBar } from "@/domains/design/components/risk-progress-bar"
import { RotateIcon } from "@/domains/icons/huge-icons/monotone"
import { cn, formatTokenOptimizationValue } from "@/domains/library"

import type { SuperVoterViewProps } from "./types"
import { EmptyStateView } from "./empty-state-view"
import { OptimizationEngineDropdown } from "./optimization-engine-dropdown"
import { RevenueSourceAllocationCardView } from "./revenue-source-allocation-card-view"

export const SuperVoterView = observer(
  ({
    totalExpectedIncome,
    poolCount,
    countdownText,
    revenueSources,
    totalAllocation,
    maxLockEnabled,
    transactionFailureRisk,
    hasSuperVoterAccess,
    selectedEngineType,
    selectedOptimizationStrategy,
    selectedTokenStrategy,
    selectedTokenForDisplay,
    tokenStrategyEstimatedAmount,
    strategies,
    lastAppliedStrategy,
    onRemovePool,
    onUpdateAllocation,
    onReset,
    onMaxLockToggle,
    onCastVote,
    onGrantAccess,
    onApplyPreviousVotes,
  }: SuperVoterViewProps) => {
    return (
      <div className="flex flex-grow flex-col overflow-hidden rounded-2xl border border-gray-800 bg-p300 shadow-[0_20px_50px_rgba(0,0,0,0.5)]">
        {/* Main Content */}
        <div className="flex-1 p-6 md:p-8 @container">
          {/* Header: Design-aligned two-column layout */}
          <div className="mb-8 flex flex-col gap-6 border-b border-gray-800 pb-6 md:flex-row md:items-start md:justify-between">
            {/* Left: SuperVoter + Selection Status & Reward Display */}
            <div className="flex-1 space-y-6 text-white">
              <SuperVoteLogo />
              {(selectedEngineType === "optimization" || selectedEngineType === "token") ? (
                <ActiveStrategyDisplay
                  selectedEngineType={selectedEngineType}
                  selectedOptimizationStrategy={selectedOptimizationStrategy}
                  selectedTokenForDisplay={selectedTokenForDisplay}
                  tokenStrategyEstimatedAmount={tokenStrategyEstimatedAmount}
                  strategies={strategies}
                />
              ) : (
                <TotalEstRewards totalExpectedIncome={totalExpectedIncome} />
              )}
            </div>

            {/* Right: Ends in countdown + Optimization Engine dropdown */}
            <div className="w-full space-y-3 md:w-auto md:min-w-[288px]">
              <div className="flex items-center justify-end gap-2 font-mono text-xs text-gray-500">
                <Clock size={14} />
                <span>
                  Ends in <span className="text-white">{countdownText}</span>
                </span>
              </div>
              <OptimizationEngineDropdown />
            </div>
          </div>

          {/* Revenue Sources Grid or Empty State */}
          {revenueSources.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 @md:grid-cols-2 @lg:grid-cols-3">
              {revenueSources.map((revenueSource) => (
                <RevenueSourceAllocationCardView
                  key={revenueSource.id}
                  revenueSource={revenueSource}
                  onRemove={onRemovePool}
                  onUpdateAllocation={onUpdateAllocation}
                />
              ))}
            </div>
          ) : (
            <EmptyStateView
              selectedEngineType={selectedEngineType}
              selectedOptimizationStrategy={selectedOptimizationStrategy}
              selectedTokenStrategy={selectedTokenStrategy}
              selectedTokenForDisplay={selectedTokenForDisplay}
              tokenStrategyEstimatedAmount={tokenStrategyEstimatedAmount}
              strategies={strategies}
              onCastVote={onCastVote}
            />
          )}
        </div>

        {/* Footer */}
        {/* <div className="hidden h-6 w-px bg-p400-light sm:block" /> */}
        {hasSuperVoterAccess ? (
          <div className="flex flex-col gap-4 border-t border-p400-light bg-p300 p-4">
            <div className="flex items-center gap-6">
              <VotingPowerUsedProgressBar totalAllocation={totalAllocation} />
              <ResetButton onReset={onReset} />
            </div>
            <div className="flex items-center justify-between gap-6">
              <Button variant="percentage" size="sm" onClick={onApplyPreviousVotes}>
                Apply previous votes
              </Button>
              <div className="flex items-center gap-6">
                {totalAllocation > 0 ? (
                  <div className="w-[80px] max-w-[80px]">
                    <RiskProgressBar
                      title="Tx Gas Limit"
                      value={transactionFailureRisk.value}
                      rank={transactionFailureRisk.rank}
                      color={transactionFailureRisk.color}
                      className="w-fit"
                      maxWidth={80}
                    />
                  </div>
                ) : null}

                <MaxLockToggle maxLockEnabled={maxLockEnabled} onMaxLockToggle={onMaxLockToggle} />
                <VoteButton
                  onCastVote={onCastVote}
                  selectedEngineType={selectedEngineType}
                  selectedOptimizationStrategy={selectedOptimizationStrategy}
                  selectedTokenForDisplay={selectedTokenForDisplay}
                  strategies={strategies}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-4 border-t border-p400-light bg-p300 p-4">
            <GrantAccessButton onGrantAccess={onGrantAccess} />
          </div>
        )}
      </div>
    )
  },
)

function getVoteButtonLabel(
  selectedEngineType: "optimization" | "token" | null,
  selectedOptimizationStrategy: VoteOptimizationStrategyType | null,
  selectedTokenForDisplay: { symbol: string } | null,
  strategies: Array<{ id: VoteOptimizationStrategyType; label: string }>,
): string {
  if (selectedEngineType === "optimization" && selectedOptimizationStrategy) {
    const strategy = strategies.find((s) => s.id === selectedOptimizationStrategy)
    return strategy ? `Vote for ${strategy.label}` : "Cast Vote"
  }
  if (selectedEngineType === "token" && selectedTokenForDisplay) {
    return `Vote for ${selectedTokenForDisplay.symbol}`
  }
  return "Cast Vote"
}

const VoteButton = ({
  onCastVote,
  selectedEngineType,
  selectedOptimizationStrategy,
  selectedTokenForDisplay,
  strategies,
}: {
  onCastVote: () => void
  selectedEngineType: "optimization" | "token" | null
  selectedOptimizationStrategy: VoteOptimizationStrategyType | null
  selectedTokenForDisplay: { symbol: string } | null
  strategies: Array<{ id: VoteOptimizationStrategyType; label: string }>
}) => {
  const label = getVoteButtonLabel(
    selectedEngineType,
    selectedOptimizationStrategy,
    selectedTokenForDisplay,
    strategies,
  )
  return (
    <button
      onClick={onCastVote}
      className="rounded-lg bg-primary-400 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-primary-900/20 transition-all hover:translate-y-[-1px] hover:bg-primary-500"
    >
      {label}
    </button>
  )
}

const GrantAccessButton = ({ onGrantAccess }: { onGrantAccess: () => void }) => {
  return (
    <button
      onClick={onGrantAccess}
      className="rounded-lg bg-primary-400 px-6 py-2 text-sm font-bold text-white shadow-lg shadow-primary-900/20 transition-all hover:translate-y-[-1px] hover:bg-primary-500"
    >
      Grant Voting Access
    </button>
  )
}

const VotingPowerUsedProgressBar = ({ totalAllocation }: { totalAllocation: number }) => {
  return (
    <div className="flex w-full items-center gap-4 sm:flex-1">
      <div className="w-12 whitespace-nowrap text-right text-sm font-medium text-t100">{totalAllocation}%</div>
      <div className="relative h-2.5 flex-1 overflow-hidden rounded-full bg-p400">
        {/* Quarter markers */}
        <div className="absolute bottom-0 left-1/4 top-0 z-10 w-px bg-p500" />
        <div className="absolute bottom-0 left-1/2 top-0 z-10 w-px bg-p500" />
        <div className="absolute bottom-0 left-3/4 top-0 z-10 w-px bg-p500" />
        {/* Progress bar */}
        <div
          className={`h-full transition-all duration-300 ${
            totalAllocation > 100 ? "bg-red-500" : "bg-primary-400"
          } shadow-[0_0_10px_rgba(37,99,235,0.5)]`}
          style={{ width: `${Math.min(100, totalAllocation)}%` }}
        />
      </div>
      <div className="hidden whitespace-nowrap text-xs text-t300 sm:block">Voting Power Used</div>
    </div>
  )
}

const ResetButton = ({ onReset }: { onReset: () => void }) => {
  return (
    <button
      onClick={onReset}
      className="rounded-lg p-2 text-t300 transition-colors hover:bg-p400 hover:text-t100"
      title="Reset"
      aria-label="Reset allocations"
    >
      <RotateIcon size={16} />
    </button>
  )
}

const MaxLockToggle = ({
  maxLockEnabled,
  onMaxLockToggle,
}: {
  maxLockEnabled: boolean
  onMaxLockToggle: (enabled: boolean) => void
}) => {
  return (
    <div className="flex items-center gap-2 text-sm text-t300">
      <button
        onClick={() => onMaxLockToggle(!maxLockEnabled)}
        className={`relative h-5 w-9 cursor-pointer rounded-full p-0.5 transition-colors ${
          maxLockEnabled ? "bg-primary-400" : "bg-p400 hover:bg-p400-light"
        }`}
        aria-label="Toggle Max Lock"
      >
        <div
          className={`h-4 w-4 rounded-full shadow-sm transition-transform ${
            maxLockEnabled ? "translate-x-4 bg-white" : "translate-x-0 bg-p200"
          }`}
        />
      </button>
      <span>Max Lock</span>
    </div>
  )
}

const STRATEGY_DESCRIPTIONS: Record<string, string> = {
  safe: "Prioritize stable, low-volatility bribe pools.",
  degen: "Maximize APR through high-multiplier pairs.",
  beast: "Aggressive yield-sniping across all protocols.",
}

function getTokenDescription(symbol: string): string {
  switch (symbol) {
    case "SCALE":
      return "Optimize for max SCALE accumulation."
    case "WETH":
      return "Prioritize WETH bribes and gas-rebates."
    case "USDC":
    case "frxUSD":
      return "Focus on stable-value bribe rewards."
    default:
      return `Focus on ${symbol} incentives.`
  }
}

const ActiveStrategyDisplay = ({
  selectedEngineType,
  selectedOptimizationStrategy,
  selectedTokenForDisplay,
  tokenStrategyEstimatedAmount,
  strategies,
}: {
  selectedEngineType: "optimization" | "token"
  selectedOptimizationStrategy: VoteOptimizationStrategyType | null
  selectedTokenForDisplay: { symbol: string; displayBalance: string; decimals: number } | null
  tokenStrategyEstimatedAmount: number
  strategies: Array<{ id: VoteOptimizationStrategyType; label: string; estimatedValue: number }>
}) => {
  const isOptimization = selectedEngineType === "optimization"
  const strategy = strategies.find((s) => s.id === selectedOptimizationStrategy)

  const badgeLabel = isOptimization ? "Active Strategy" : "Focusing Incentives"
  const badgeClass = isOptimization
    ? "bg-blue-500/10 border-blue-500/20 text-blue-400"
    : "bg-orange-500/10 border-orange-500/20 text-orange-400"
  const BadgeIcon = isOptimization ? TrendingUp : Target

  const value =
    isOptimization && strategy
      ? `$${strategy.estimatedValue.toFixed(2)}`
      : selectedTokenForDisplay
        ? formatTokenOptimizationValue(
            tokenStrategyEstimatedAmount.toString(),
            selectedTokenForDisplay.decimals,
          )
        : "—"
  const unit = isOptimization ? "USD" : selectedTokenForDisplay?.symbol ?? ""
  const description =
    isOptimization && strategy
      ? STRATEGY_DESCRIPTIONS[strategy.id] ?? "Optimized for best returns."
      : selectedTokenForDisplay
        ? getTokenDescription(selectedTokenForDisplay.symbol)
        : ""

  return (
    <div className="animate-in fade-in slide-in-from-left-4 duration-500">
      <div className="mb-2 flex items-center gap-3">
        <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{badgeLabel}</span>
        <div className={`flex items-center gap-1.5 rounded border px-2 py-0.5 text-[10px] font-bold ${badgeClass}`}>
          <BadgeIcon size={10} />
          {isOptimization && strategy ? strategy.label.toUpperCase() : selectedTokenForDisplay?.symbol ?? ""}
        </div>
      </div>
      <div className="flex items-baseline gap-3">
        <h2 className="text-6xl font-bold tracking-tight text-white">{value}</h2>
        <span className="text-2xl font-medium text-gray-600">{unit}</span>
      </div>
      {description ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-gray-500">
          <Info size={14} className="text-gray-700" />
          {description}
        </p>
      ) : null}
    </div>
  )
}

const TotalEstRewards = ({ totalExpectedIncome }: { totalExpectedIncome: number }) => {
  return (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500">Total Est. Rewards</div>
      <div className="flex items-baseline gap-3">
        <span className="text-6xl font-bold tracking-tight text-white">${totalExpectedIncome.toFixed(2)}</span>
        <span className="text-2xl font-medium text-gray-600">USD</span>
      </div>
    </div>
  )
}
